import logoLightBg from "@/assets/prism_logo.svg"
import logoDarkBg from "@/assets/prism_logo_on_dark.svg"
import { cn } from "@/lib/utils"

// Native aspect ratio of the Prism lockup (viewBox 0 0 907.39 190.16, cropped
// to the artwork). The previous BYLD lockup was 3.246 — this is wider, so the
// same named height produces a noticeably longer mark. See SIZES.
const ASPECT = 907.39 / 190.16 // ≈ 4.772

/**
 * Sizes are named by CONTEXT, not by a t-shirt scale, because the binding
 * constraint is the container width — not a visual preference.
 *
 * These are the BYLD heights (40/44/48) cut by a further 25% on review of the
 * Prism lockup rendered live. Two changes of reasoning got here, and both
 * matter if anyone is tempted to put the old numbers back.
 *
 * The BYLD asset carried a tagline whose x-height needed real HEIGHT to read,
 * which is why its heights were argued about at all. The Prism lockup has no
 * tagline, so height is no longer a legibility floor and nothing is lost by
 * going smaller. What replaced height as the constraint is WIDTH: at 4.772:1
 * against BYLD's 3.246:1, the same height draws a much longer mark, and at the
 * inherited heights it simply read as too big.
 *
 *   sidebar — 30px tall → 143px wide
 *   compact — 33px tall → 157px wide
 *   page    — 36px tall → 172px wide
 *
 * The three sidebars remain the tight case and now have real headroom rather
 * than a near miss. Measured in the browser against their actual CSS: 215px of
 * content width at px-5 and 207px at px-6, so 72px and 64px spare — where the
 * inherited heights left 24px and 16px. The logo is the only child of the link
 * in all three, so it does not share that budget, and maxWidth:100% below is
 * the backstop if a future sidebar is narrower.
 */
const SIZES = { sidebar: 30, compact: 33, page: 36 } as const

/**
 * Which ground this logo is being drawn on.
 *
 * "auto" — the normal case. Both variants are rendered and CSS picks, so the
 *          logo follows the platform theme with no JavaScript involved.
 * "light-bg" — force the navy wordmark. For the three places whose background
 *          is a hardcoded `bg-white` that does NOT flip with the theme, where
 *          "auto" would put a white wordmark on white the moment a user turns
 *          dark mode on.
 */
export type BrandLogoTone = "auto" | "light-bg"

export interface BrandLogoProps {
  /** Named context size, or an explicit height in px. Width follows the aspect ratio. */
  size?: keyof typeof SIZES | number
  className?: string
  /** Accessible name. Defaults to "Prism" — the lockup stands alone with no adjacent text. */
  alt?: string
  /** See BrandLogoTone. Defaults to "auto". */
  tone?: BrandLogoTone
}

/**
 * The Prism lockup — cube mark + "PRISM" wordmark.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * TWO FILES, ONE FOR EACH GROUND, AND WHY IT IS NOT ONE FILE WITH currentColor.
 *
 * The wordmark has to be navy on light and white on dark: measured against the
 * app's real backgrounds, navy scores 1.03–1.25:1 on the dark theme and white
 * scores 1.00:1 on the light one. Neither colour works for both, and light is
 * the default theme, so a single hardcoded colour is wrong somewhere.
 *
 * currentColor would be the elegant answer and it does not work here. This
 * renders the logo as <img src=…>, and an SVG loaded through <img> is an
 * isolated document that the host page's CSS does not reach — currentColor
 * resolves against the SVG's own root and comes out BLACK. That was measured in
 * Chrome, not assumed: the same markup inline in the DOM inherits the page
 * colour correctly, through <img> it does not. Inlining would need svgr (not a
 * dependency, deliberately) or a hand-written component, and would duplicate
 * the lockup's three gradient ids on every instance.
 *
 * WHY CSS PICKS AND NOT useTheme(). Dark mode here is a `dark` class on <html>
 * (useDarkMode), and index.html applies it synchronously before React mounts.
 * Letting Tailwind's `dark:` variants choose therefore gets the right variant in
 * the FIRST paint, with no hook, no context dependency and no flash. It also
 * matches how the rest of the app handles this — MultiraterTakePage writes
 * `bg-[#F8F9FB] dark:bg-slate-900` for exactly the same reason.
 *
 * BOTH <img> TAGS CARRY THE SAME alt, WHICH IS NOT A DOUBLE ANNOUNCEMENT.
 * Tailwind's `hidden` is display:none, and assistive technology skips
 * display:none subtrees, so exactly one of the two is ever exposed. Giving the
 * inactive one alt="" instead would be wrong, because WHICH one is inactive is
 * a CSS fact this component cannot know.
 *
 * Rendered as <img> from the SVG — Vite resolves the import to a URL (no svgr
 * plugin), so the files are cached separately rather than inlined into the
 * bundle. Both variants are fetched in the "auto" case; they are ~5.7 KB each
 * and differ only in one fill.
 *
 * NOTE: this is the PLATFORM's own branding, unrelated to a client's uploaded
 * logo (ClientAdmin.logoUrl), which is per-tenant and stays dynamic.
 * ────────────────────────────────────────────────────────────────────────────
 */
export function BrandLogo({
  size = "page",
  className,
  alt = "Prism",
  tone = "auto",
}: BrandLogoProps) {
  const height = typeof size === "number" ? size : SIZES[size]
  const width = Math.round(height * ASPECT)
  // Explicit width/height reserve the box before the SVG loads (no layout shift).
  const common = {
    alt,
    width,
    height,
    style: { height, width: "auto", maxWidth: "100%" } as const,
    draggable: false as const,
  }

  if (tone === "light-bg") {
    return (
      <img
        {...common}
        src={logoLightBg}
        className={cn("block shrink-0 select-none", className)}
      />
    )
  }

  return (
    <>
      <img
        {...common}
        src={logoLightBg}
        className={cn("block shrink-0 select-none dark:hidden", className)}
      />
      <img
        {...common}
        src={logoDarkBg}
        className={cn("hidden shrink-0 select-none dark:block", className)}
      />
    </>
  )
}
